import { LumberProfileCatalog } from "building-types/framing/lumberTypes";
import { RoofFramingComponentType } from "building-types/framing/roofFramingComponentTypes";
import { RoofFramingType, type RoofTruss } from "building-types/framing/roofFramingTypes";
import type { RoofTrussBasicInput } from "building-types/framing/roofTrussTypes";
import { RoofTrussType } from "building-types/framing/roofTrussTypes";
import { unsignedNumberNew } from "common/utils/nominalNumbers";
import { lumberPiecesByProfileNew } from "doc/utils/lumberTools";
import { planeNormal } from "geom/plane";
import type { TrussGenerator } from "./trussGenerator";
import {
  generateBottomChord,
  generateCenterDiagonalWebChords,
  generateCenterVerticalWebChord,
  generateTopChords,
  projectChordsToFramingPlane,
  setupBottomChordRails,
  setupTopChordRails,
  setupWebChordRails,
} from "./trussUtils";

export class QueenPostTrussGenerator implements TrussGenerator {
  generate(input: RoofTrussBasicInput): RoofTruss {
    const {
      leftSpan,
      leftEaveFasciaSize,
      rightSpan,
      rightEaveFasciaSize,
      leftOverhang,
      rightOverhang,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordProfile,
      bottomChordProfile,
      webChordProfile,
      ridgeRafterProfile,
      bottomChordOffset,
      trussPlane,
    } = input;

    if (Array.isArray(leftSpan) || Array.isArray(rightSpan)) {
      throw new Error("Queen post truss does not support multiple spans");
    }

    const ridgeRafterHalfHeight = unsignedNumberNew(LumberProfileCatalog[ridgeRafterProfile].width / 2);
    const webChordHalfHeight = unsignedNumberNew(LumberProfileCatalog[webChordProfile].width / 2);
    const bottomChordHeight = unsignedNumberNew(LumberProfileCatalog[bottomChordProfile].width);
    const topChordHeight = unsignedNumberNew(LumberProfileCatalog[topChordProfile].width);
    const topChordHalfHeight = unsignedNumberNew(topChordHeight / 2);

    const { left, right } = generateTopChords(leftSpan, rightSpan, leftTopChordPitch, rightTopChordPitch);
    const {
      bottomChord: bottomChordTopPoints,
      leftHeelChord,
      rightHeelChord,
    } = generateBottomChord(
      leftSpan,
      rightSpan,
      leftOverhang,
      rightOverhang,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordHeight,
      bottomChordHeight,
      bottomChordOffset,
    );

    const webChordCenterPoints = generateCenterVerticalWebChord(
      webChordHalfHeight,
      bottomChordTopPoints[5] - bottomChordHeight / 2,
      ridgeRafterHalfHeight,
    );

    const [leftWebChord, rightWebChord] = generateCenterDiagonalWebChords(
      webChordHalfHeight,
      bottomChordTopPoints[5],
      leftSpan,
      leftOverhang,
      rightSpan,
      rightOverhang,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordHalfHeight,
    );

    const [
      leftTopChordTopPointsToFramingPlane,
      rightTopChordTopPointsToFramingPlane,
      bottomChordTopPointsToFramingPlane,
      webChordCenterPointsToFramingPlane,
      leftWebChordToFramingPlane,
      rightWebChordToFramingPlane,
      leftHeelChordToFramingPlane,
      rightHeelChordToFramingPlane,
    ] = projectChordsToFramingPlane(
      [
        left,
        right,
        bottomChordTopPoints,
        webChordCenterPoints,
        leftWebChord,
        rightWebChord,
        leftHeelChord,
        rightHeelChord,
      ],
      trussPlane,
    );

    const topChords = lumberPiecesByProfileNew(LumberProfileCatalog[topChordProfile], { [topChordProfile]: [] });
    const bottomChords = lumberPiecesByProfileNew(LumberProfileCatalog[bottomChordProfile], {
      [bottomChordProfile]: [],
    });
    const webChords = lumberPiecesByProfileNew(LumberProfileCatalog[webChordProfile], { [webChordProfile]: [] });
    const [normX, normY, normZ] = planeNormal(trussPlane);

    setupTopChordRails(topChords, leftTopChordTopPointsToFramingPlane, leftEaveFasciaSize, normX, normY, normZ);
    setupTopChordRails(topChords, rightTopChordTopPointsToFramingPlane, rightEaveFasciaSize, normX, normY, normZ);

    setupBottomChordRails(bottomChords, bottomChordTopPointsToFramingPlane, normX, normY, normZ);
    [
      webChordCenterPointsToFramingPlane,
      leftWebChordToFramingPlane,
      rightWebChordToFramingPlane,
      leftHeelChordToFramingPlane,
      rightHeelChordToFramingPlane,
    ].forEach((webChord) => {
      setupWebChordRails(webChords, webChord, normX, normY, normZ);
    });

    return {
      trussType: RoofTrussType.QUEEN_POST_TRUSS,
      components: [
        { type: RoofFramingComponentType.TOP_CHORD, lumber: topChords },
        { type: RoofFramingComponentType.BOTTOM_CHORD, lumber: bottomChords },
        { type: RoofFramingComponentType.WEB, lumber: webChords },
      ],
      framingType: RoofFramingType.TRUSS,
      plane: trussPlane,
    };
  }
}
